/**
 * UpgradeContextModal - Generic upgrade modal for all limit scenarios
 * Handles different UI based on user role and payment method status:
 * - Owner + No CC: Redirect to Stripe Checkout
 * - Owner + CC: One-click upgrade with confirmation
 * - PM/Member + Seats Available: Claim seat option
 * - PM/Member + No Seats: Request upgrade from owner
 */

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  AlertTriangle, 
  Sparkles, 
  ArrowRight, 
  Package, 
  Loader2, 
  Users, 
  Layers, 
  Zap,
  Mail,
  CreditCard,
  Check,
  Crown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { billingApi, stripeApi } from '../../services/api';
import { toast } from 'sonner';
import { saveOperationState } from '../../hooks/useOperationState';
import UpgradeConfirmDialog from './UpgradeConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';

// Limit type icons and labels
const LIMIT_CONFIG = {
  products: {
    icon: Package,
    label: 'Product Limit',
    description: 'You\'ve reached the maximum number of products on your current plan.',
  },
  features: {
    icon: Layers,
    label: 'Feature Limit',
    description: 'You\'ve reached the maximum number of features on your current plan.',
  },
  seats: {
    icon: Users,
    label: 'Team Seat Limit',
    description: 'You\'ve reached the maximum number of team seats on your current plan.',
  },
  ai_usage: {
    icon: Zap,
    label: 'AI Usage Limit',
    description: 'You\'ve reached your AI request limit for this billing period.',
  },
};

const UpgradeContextModal = ({
  isOpen,
  onClose,
  limitType = 'products',
  currentUsage = null,
  currentLimit = null,
  wizardState = null,  // Optional: state to preserve for resume
  returnPath = null,   // Optional: path to return to after upgrade
  contextMessage = null, // Optional: custom context message to display
  onUpgradeComplete,   // Callback when upgrade is complete (one-click)
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { organization } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  
  const limitConfig = LIMIT_CONFIG[limitType] || LIMIT_CONFIG.products;
  const LimitIcon = limitConfig.icon;
  
  // Fetch upgrade context when modal opens
  useEffect(() => {
    const fetchContextData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await billingApi.getUpgradeContext(limitType);
        setContext(response.data);
        
        // Auto-select recommended plan
        if (response.data.recommended_plan_id) {
          const recommended = response.data.upgrade_options?.find(
            p => p.plan_id === response.data.recommended_plan_id
          );
          if (recommended) {
            setSelectedPlan(recommended);
          }
        }
      } catch (err) {
        console.error('Failed to fetch upgrade context:', err);
        setError('Failed to load upgrade options. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      fetchContextData();
    }
  }, [isOpen, limitType]);
  
  const fetchContext = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await billingApi.getUpgradeContext(limitType);
      setContext(response.data);
      
      // Auto-select recommended plan
      if (response.data.recommended_plan_id) {
        const recommended = response.data.upgrade_options?.find(
          p => p.plan_id === response.data.recommended_plan_id
        );
        if (recommended) {
          setSelectedPlan(recommended);
        }
      }
    } catch (err) {
      console.error('Failed to fetch upgrade context:', err);
      setError('Failed to load upgrade options. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle Stripe Checkout redirect (for users without payment method)
  const handleCheckoutRedirect = async () => {
    setActionLoading(true);
    
    try {
      // plan_id is required - get from organization or selected plan
      const planId = selectedPlan?.plan_id || organization?.plan_id;
      if (!planId) {
        toast.error('No plan selected. Please contact support.');
        setActionLoading(false);
        return;
      }
      
      // Save state before redirect
      if (wizardState) {
        saveOperationState(
          `${limitType}_creation`,
          wizardState,
          returnPath || window.location.pathname,
          'upgrade'
        );
      }
      
      // Build return URLs
      const baseUrl = window.location.origin;
      const path = returnPath || window.location.pathname;
      const successUrl = `${baseUrl}${path}?resume=true&checkout=success`;
      const cancelUrl = `${baseUrl}${path}?resume=true&checkout=cancelled`;
      
      const response = await stripeApi.createCheckoutSession({
        seats: 1,
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
        setActionLoading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const message = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setActionLoading(false);
    }
  };
  
  // Handle one-click upgrade (for users with payment method)
  const handleOneClickUpgrade = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    
    setShowConfirmDialog(true);
  };
  
  // Confirm one-click upgrade
  const confirmUpgrade = async () => {
    setActionLoading(true);
    setShowConfirmDialog(false);
    
    try {
      const response = await billingApi.oneClickUpgrade(selectedPlan.plan_id);
      
      if (response.data?.success) {
        toast.success(`Upgraded to ${selectedPlan.label}! 🎉`, {
          description: 'Your plan has been upgraded successfully.',
        });
        
        onUpgradeComplete?.();
        onClose();
      } else {
        toast.error('Upgrade failed. Please try again.');
      }
    } catch (err) {
      console.error('One-click upgrade error:', err);
      const message = err.response?.data?.detail || 'Upgrade failed';
      
      // If no subscription, fall back to checkout
      if (message.includes('checkout') || message.includes('subscription')) {
        toast.info('Redirecting to checkout...', {
          description: 'Please complete the payment to activate your subscription.',
        });
        handleCheckoutRedirect();
      } else {
        toast.error(message);
        setActionLoading(false);
      }
    }
  };
  
  // Handle upgrade request (for PMs/Members)
  const handleRequestUpgrade = async () => {
    setActionLoading(true);
    
    try {
      await billingApi.requestUpgrade(limitType, requestMessage || null);
      
      toast.success('Upgrade request sent! 📧', {
        description: `${context.owner_name || 'The organization owner'} has been notified.`,
      });
      
      onClose();
    } catch (err) {
      console.error('Request upgrade error:', err);
      toast.error('Failed to send upgrade request');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Render loading state
  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn("max-w-md", colors.bgCard, colors.borderPrimary)}>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn("max-w-md", colors.bgCard, colors.borderPrimary)}>
          <DialogHeader>
            <DialogTitle className="text-red-600">Error</DialogTitle>
          </DialogHeader>
          <p className={colors.textSecondary}>{error}</p>
          <DialogFooter>
            <Button variant="outline" onClick={fetchContext}>Retry</Button>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Determine usage values
  const usage = currentUsage ?? context?.current_usage ?? 0;
  const limit = currentLimit ?? context?.current_limit ?? 0;
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn("max-w-lg", colors.bgCard, colors.borderPrimary)}>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <LimitIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <DialogTitle className={cn("text-xl", colors.textPrimary)}>
                  {limitConfig.label} Reached
                </DialogTitle>
                <DialogDescription className={cn("text-sm mt-1", colors.textSecondary)}>
                  {contextMessage || limitConfig.description}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="py-4 space-y-5">
            {/* Current Usage Display */}
            <div className="flex items-center justify-center gap-4">
              <div className={cn("p-4 rounded-lg text-center flex-1", colors.bgTertiary)}>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <LimitIcon className="w-5 h-5 text-blue-500" />
                  <span className={cn("text-2xl font-bold", colors.textPrimary)}>{usage}</span>
                </div>
                <div className={cn("text-xs", colors.textMuted)}>Used</div>
              </div>
              <div className={cn("text-2xl font-bold", colors.textMuted)}>/</div>
              <div className={cn("p-4 rounded-lg text-center flex-1", colors.bgTertiary)}>
                <div className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {limit || '∞'}
                </div>
                <div className={cn("text-xs", colors.textMuted)}>
                  {context?.current_plan_label || 'Free'} Limit
                </div>
              </div>
            </div>
            
            {/* Plan Seat Limit Warning - For seats limit type */}
            {limitType === 'seats' && context?.plan_seat_limit && 
             context?.seats_total >= context.plan_seat_limit && (
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Plan Seat Limit Reached
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Your plan allows a maximum of {context.plan_seat_limit} seats. 
                      Contact support to upgrade your plan limit, or free up seats by 
                      demoting team members to Collaborator role (free).
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Upgrade Options based on user role and payment status */}
            <div className="space-y-3">
              {context?.can_upgrade ? (
                // Owner/Billing Admin view
                <>
                  {context.has_payment_method ? (
                    // One-click upgrade option
                    <div className="space-y-3">
                      <p className={cn("text-sm font-medium", colors.textSecondary)}>
                        <CreditCard className="w-4 h-4 inline mr-2" />
                        Payment method on file - Upgrade instantly
                      </p>
                      
                      {context.upgrade_options?.map((plan) => (
                        <button
                          key={plan.plan_id}
                          onClick={() => {
                            setSelectedPlan(plan);
                            handleOneClickUpgrade();
                          }}
                          disabled={actionLoading}
                          className={cn(
                            "w-full p-4 rounded-lg border-2 text-left transition-all hover:shadow-lg",
                            selectedPlan?.plan_id === plan.plan_id
                              ? "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-400 dark:border-purple-600"
                              : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300",
                            actionLoading && "opacity-70 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                              {actionLoading && selectedPlan?.plan_id === plan.plan_id ? (
                                <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                              ) : (
                                <Crown className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-purple-700 dark:text-purple-300">
                                  {plan.label}
                                </p>
                                {plan.price_display && (
                                  <span className="text-sm font-bold text-purple-600">
                                    {plan.price_display}
                                  </span>
                                )}
                              </div>
                              <ul className="text-xs mt-2 space-y-1 text-gray-600 dark:text-gray-400">
                                {plan.features?.slice(0, 3).map((feature, i) => (
                                  <li key={i} className="flex items-center gap-1">
                                    <Check className="w-3 h-3 text-green-500" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <ArrowRight className="w-5 h-5 mt-2 shrink-0 text-purple-500" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    // Redirect to Stripe Checkout
                    <button
                      onClick={handleCheckoutRedirect}
                      disabled={actionLoading}
                      className={cn(
                        "w-full p-4 rounded-lg border-2 text-left transition-all hover:shadow-lg",
                        "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20",
                        "border-purple-400 dark:border-purple-600 hover:border-purple-500",
                        actionLoading && "opacity-70 cursor-not-allowed"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                          {actionLoading ? (
                            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                          ) : (
                            <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-purple-700 dark:text-purple-300 text-lg">
                            {actionLoading ? 'Redirecting to checkout...' : 'Upgrade to Pro'}
                          </p>
                          <p className="text-sm mt-1 text-purple-600/80 dark:text-purple-400/80">
                            Unlock <span className="font-medium">unlimited {limitType}</span>, 
                            advanced AI features, and team collaboration.
                          </p>
                          {context.upgrade_options?.[0]?.price_display && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                              <span className="font-semibold">{context.upgrade_options[0].price_display}</span>
                              <span className="text-purple-400">•</span>
                              <span>Cancel anytime</span>
                            </div>
                          )}
                        </div>
                        {!actionLoading && <ArrowRight className="w-5 h-5 mt-3 shrink-0 text-purple-500" />}
                      </div>
                    </button>
                  )}
                </>
              ) : context?.can_request_upgrade ? (
                // PM/Member view - Request upgrade
                <div className="space-y-4">
                  <div className={cn("p-4 rounded-lg border", colors.bgTertiary, "border-blue-200 dark:border-blue-800")}>
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className={cn("font-medium", colors.textPrimary)}>
                          Request Upgrade from Admin
                        </p>
                        <p className={cn("text-sm mt-1", colors.textSecondary)}>
                          Your organization owner ({context.owner_name || context.owner_email || 'admin'}) 
                          will be notified via email and in-app.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className={cn("text-sm font-medium", colors.textSecondary)}>
                      Add a message (optional)
                    </label>
                    <textarea
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      placeholder="I need more capacity to complete the sprint planning..."
                      className={cn(
                        "w-full p-3 rounded-lg border text-sm resize-none",
                        colors.bgTertiary, colors.borderPrimary, colors.textPrimary
                      )}
                      rows={2}
                    />
                  </div>
                  
                  <Button
                    onClick={handleRequestUpgrade}
                    disabled={actionLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Request Upgrade
                  </Button>
                </div>
              ) : (
                // No upgrade path available
                <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary)}>
                  <p className={colors.textSecondary}>
                    Please contact your administrator for upgrade options.
                  </p>
                </div>
              )}
              
              {/* Archive option hint */}
              {limitType === 'products' && (
                <div className={cn("p-3 rounded-lg border text-center", colors.bgTertiary, "border-gray-300 dark:border-gray-600")}>
                  <p className={cn("text-sm", colors.textSecondary)}>
                    Or archive an existing {limitType.slice(0, -1)} to free up a slot.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button 
              variant="outline" 
              onClick={onClose} 
              disabled={actionLoading}
              className={cn("border-gray-300 dark:border-gray-600", colors.textPrimary)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirmation dialog for one-click upgrade */}
      <UpgradeConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={confirmUpgrade}
        plan={selectedPlan}
        isLoading={actionLoading}
      />
    </>
  );
};

export default UpgradeContextModal;
